/**
 * Creates a wrapped object around a Storage interface, such as localStorage or
 * sessionStorage.  All data is namespaced to avoid collisions with other storage.
 *
 * This implementation stores data using compound keys:
 * ```
 * optimizely_data$$[endUserId]$$[projectId]$$keyName = data...
 * ```
 *
 * @module
 */
var _ = require('optimizely-client-fn');
var Logger = require('../utils/logger');

/**
 * Get value for key
 *
 * @callback getItemFunction
 * @param {string} key
 */

/**
 * Set value for key
 *
 * @callback setItemFunction
 * @param {string} key
 * @param {string} value
 */

/**
 * Remove key
 *
 * @callback removeItemFunction
 * @param {string} key
 */

/**
 * @typedef {Object} StorageInterface
 * @property {getItemFunction} getItem
 * @property {setItemFunction} setItem
 * @property {removeItemFunction} [removeItem]
 */

/**
 * @constructor
 * @param {Storage} storage
 * @param {string} baseKey
 */
function StorageWrapper(storage:Storage, baseKey:string) {
  this.__storage = storage;
  this.__baseKey = baseKey;
}

var DELIMITER = '$$';

/**
 * Get the key path for a given key, by joining with this.__baseKey using the delimiter "$$".
 * @param {string} key
 * @returns {string}
 * @private
 */
StorageWrapper.prototype.__getKeyPath = function(key:string): string {
  return [this.__baseKey, key].join(DELIMITER);
};

StorageWrapper.prototype.__getKeyFromStorageKey = function(storageKey) {
  return storageKey.replace(this.__baseKey + DELIMITER, '');
};

/**
 * Sets an item by key name.
 * @param {string} key
 * @param {string} value
 */
StorageWrapper.prototype.setItem = function(key:string, value:string) {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('localstorageSetItem');
  try {
    this.__storage.setItem(this.__getKeyPath(key), value);
  } catch(e) {
    Logger.warn('Failed to save', key, 'to localStorage:', e);
  }
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('localstorageSetItem');
};

/**
 * Removes an item by key name.
 * @param {string} key
 */
StorageWrapper.prototype.removeItem = function(key:string) {
  this.__storage.removeItem(this.__getKeyPath(key));
};

/**
 * Gets an item by key name.
 * @param {string} key
 * @return {string|null} Value of key, or null if not found
 */
StorageWrapper.prototype.getItem = function(key:string): string|null {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('localstorageGetItem');
  var getData = null;
  try {
    getData = this.__storage.getItem(this.__getKeyPath(key));
  } catch(e) {} // eslint-disable-line
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('localstorageGetItem');
  return getData;
};

StorageWrapper.prototype.keys = function() {
  var keys = _.keys(this.__storage);
  return _.map(_.filter(keys, _.bind(function(key) {
    return _.includes(key, this.__baseKey);
  }, this)), _.bind(this.__getKeyFromStorageKey, this));
};

StorageWrapper.prototype.allKeys = function() {
  return _.keys(this.__storage);
};

StorageWrapper.prototype.allValues = function() {
  return _.values(this.__storage);
};

module.exports = {
  /**
   * @param {Storage} storage
   * @param {string} baseKey
   * @return {StorageWrapper}
   */
  create: function(storage:Storage, baseKey:string): import('core/lib/storage_factory').StorageWrapper {
    return new StorageWrapper(storage, baseKey);
  },
  /**
   * Expose a mocked interface to use if there is no localStorage
   */
  mockStorage: {
    keys: function() {},
    getItem: function(key) {},
    removeItem: function(key) {},
    setItem: function(key, value) {},
  },
};
